import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'

import { Access } from '../../lib'
import * as state from '../../states'
import { IAccountBuyer, EAccountIsFired } from '../../types'
import { IUiTableConfig, UiTable } from '../ui'
import { useMultiSelectTable } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'
import { getAccountIsFiredBadge } from '../account'

type IRevenueSpendUiSettingsTableProps = {
	revenueAgencyId: number
	items: Array<IAccountBuyer>
	skip: number
	onMultiSelect?: (selectedItems: IAccountBuyer[]) => void
}

export const RevenueSpendUiSettingsTable = (props: IRevenueSpendUiSettingsTableProps) => {
	const {
		revenueAgencyId,
		items = [],
		skip = 0,
		onMultiSelect
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const revenueSpendBuyersSaved = useMemo(
		() => profile?.setup?.ui?.revenueSpendBuyers?.[revenueAgencyId] ?? [],
		[profile, revenueAgencyId]
	)
	const $access = new Access(profile ?? null)

	const { multiSelectConfig, selectedRowIndexes } = useMultiSelectTable(
		{
			items,
			isEnabled: true,
			onMultiSelect,
			initialSelectedRowIndexes: revenueSpendBuyersSaved
		}
	)
	//console.log('RevenueSpendUiSettingsTable: ', {selectedRowIndexes})
	const revenueSpendUiSettingsTableConfig: IUiTableConfig<IAccountBuyer>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.account_list_table.column_id)
		},
		{
			key: 'email',
			title: t(ALL_LANG_KEY.account_list_table.column_email)
		},
		{
			key: 'auth.login',
			title: t(ALL_LANG_KEY.account_list_table.column_login),
			rowCellFormatter: ({ rowData }) => {
				const valueFormat: string = rowData?.auth?.login ?? ''
				return valueFormat
			}
		},
		{
			key: 'extra.keitaroLogin',
			title: t(ALL_LANG_KEY.account_list_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'role.extra.inGroup',
			title: t(ALL_LANG_KEY.account_list_table.column_in_group),
			rowCellFormatter: ({ rowData }) => {
				const valueFormat: string = rowData?.role?.extra?.inGroup ?? ''
				return valueFormat
			}
		},
		{
			key: 'role.extra.isTeam',
			title: t(ALL_LANG_KEY.account_list_table.column_is_team),
			rowCellFormatter: ({ rowData }) => {
				const valueFormat: string = rowData?.role?.extra?.isTeam ?? ''
				return valueFormat
			}
		},
		{
			key: 'isFired',
			title: t(ALL_LANG_KEY.account_list_table.column_is_fired),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				return <>
					{getAccountIsFiredBadge(rowData.isFired, t, false, true)}
				</>
			}
		}
	]

	const tableConfig = [multiSelectConfig, ...revenueSpendUiSettingsTableConfig]

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			selectedRowIndexes={selectedRowIndexes}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper table-sticky-wrapper--top-60',
			// }}
		/>
	</>
}